import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Card } from '@xing-com/card';
import { capitalizeFirstLetter } from '@xing-com/crate-jobs-domain-serps-helpers';
import {
  useSeoJobRoleSalary,
  useSerpsMatch,
} from '@xing-com/crate-jobs-domain-serps-hooks';
import { mediaConfined } from '@xing-com/layout-tokens';
import {
  scale150,
  space4XL,
  spaceL,
  spaceM,
  spaceS,
  spaceXL,
} from '@xing-com/tokens';
import { BodyCopy, Headline, Hero } from '@xing-com/typography';

const getFormattedNumber = (value: number): string =>
  value.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export const Container = styled.div`
  margin-bottom: ${space4XL};
`;

export const Title = styled(Headline)`
  margin-bottom: ${spaceL};
  @media ${mediaConfined} {
    margin-bottom: ${spaceXL};
  }
`;

export const SalaryCard = styled(Card)`
  margin-bottom: ${spaceL};
  padding: ${spaceXL};
  width: 100%;

  @media ${mediaConfined} {
    margin-bottom: ${spaceXL};
  }
`;

export const SalaryRange = styled(Hero)`
  font-weight: 700;
  margin: 0 0 ${spaceXL};
  margin-bottom: ${spaceS};
  padding: 0;
  font-size: ${scale150};
  line-height: 1.2;

  @media ${mediaConfined} {
    margin-bottom: ${spaceM};
  }
`;

export const SalaryData: React.FC = () => {
  const { formatMessage } = useIntl();
  const serps = useSerpsMatch();
  const { data } = useSeoJobRoleSalary();
  const { rangeStart, rangeEnd } = data;
  const isStatisticsEmpty = !rangeStart || !rangeEnd;

  if (isStatisticsEmpty) {
    return null;
  }

  return (
    <Container>
      <Title size="large" sizeConfined="xlarge">
        {formatMessage(
          { id: 'XJM_JOBS_SEO_ROLE_SALARY_HEADER' },
          { jobRole: capitalizeFirstLetter(serps?.categoryValue || '') }
        )}
      </Title>

      <SalaryCard>
        <SalaryRange as="div">
          {getFormattedNumber(rangeStart || 0)}
          {' € – '}
          {getFormattedNumber(rangeEnd || 0)} €
        </SalaryRange>
        <BodyCopy size="large" lineHeight="dense" noMargin>
          <FormattedMessage
            id="XJM_JOBS_SEO_ROLE_SALARY_DESCRIPTION"
            values={{
              jobRole: capitalizeFirstLetter(serps?.categoryValue || ''),
              maximumSalary: (
                <strong>{getFormattedNumber(rangeEnd || 0)}€</strong>
              ),
              minimumSalary: (
                <strong>{getFormattedNumber(rangeStart || 0)}€</strong>
              ),
            }}
          />
        </BodyCopy>
      </SalaryCard>
    </Container>
  );
};
